import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Contact.css';

import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaClock,
  FaSearch,
  FaShieldAlt,
  FaBoxOpen,
  FaTruck,
  FaHome,
  FaFileAlt,
  FaGlobeAsia,
  FaCheckCircle,
} from 'react-icons/fa';

const API_URL =process.env.REACT_APP_API_URL || 'http://localhost:5000/';


/* =========================================================
   FILE DROP ZONE
   ========================================================= */

function FileDropZone({
  accept,
  maxSizeMB,
  onFileSelect,
  onFileError,
  file,
  label,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const validateAndSet = useCallback(
    (selectedFile) => {
      if (!selectedFile) return;

      if (selectedFile.size > maxSizeMB * 1024 * 1024) {
        if (onFileError) {
          onFileError(
            `File is too large. Maximum size is ${maxSizeMB} MB.`
          );
        }
        return;
      }

      if (onFileError) {
        onFileError(null);
      }

      onFileSelect(selectedFile);
    },
    [maxSizeMB, onFileSelect, onFileError]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);

      const droppedFile = e.dataTransfer.files[0];

      if (droppedFile) {
        validateAndSet(droppedFile);
      }
    },
    [validateAndSet]
  );

  return (
    <div
      className={`file-drop-zone${dragOver ? ' drag-over' : ''}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files[0]) {
            validateAndSet(e.target.files[0]);
          }
        }}
      />

      {file ? (
        <div className="file-preview">
          <span className="file-icon">
            <FaFileAlt />
          </span>

          <span className="file-name">
            {file.name}
          </span>

          <span className="file-size">
            ({(file.size / 1024).toFixed(1)} KB)
          </span>

          <button
            type="button"
            className="file-remove"
            onClick={(e) => {
              e.stopPropagation();
              onFileSelect(null);
              if (onFileError) {
                onFileError(null);
              }
            }}
          >
            ×
          </button>
        </div>
      ) : (
        <div className="file-placeholder">
          <span>
            <FaFileAlt />
          </span>

          <p>
            {label ||
              'Drag & drop a file here, or click to browse'}
          </p>

          <small>
            Accepted: {accept} — Max {maxSizeMB} MB
          </small>
        </div>
      )}
    </div>
  );
}


/* =========================================================
   STATUS MESSAGE
   ========================================================= */

function StatusMsg({ status, message }) {
  if (!status) return null;

  return (
    <div
      className={`alert ${
        status === 'success'
          ? 'alert-success'
          : 'alert-error'
      }`}
    >
      {status === 'success' ? '✓' : '✕'} {message}
    </div>
  );
}


/* =========================================================
   MAIN CONTACT PAGE
   ========================================================= */

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [attachment, setAttachment] = useState(null);
  const [attachmentError, setAttachmentError] =
    useState(null);

  const [submitting, setSubmitting] = useState(false);

  const [submitStatus, setSubmitStatus] =
    useState(null);

  const [submitMsg, setSubmitMsg] = useState('');


  /* =========================================================
     FORM CHANGE
     ========================================================= */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  /* =========================================================
     FORM SUBMIT
     ========================================================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (attachmentError) return;

    setSubmitting(true);
    setSubmitStatus(null);
    setSubmitMsg('');

    const data = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      data.append(key, value);
    });

    if (attachment) {
      data.append('attachment', attachment);
    }

    try {
      const response = axios.post(`${process.env.REACT_APP_API_URL}api/contact`, formData)

      setSubmitStatus('success');

      setSubmitMsg(
        response.data?.message ||
          'Thank you! Your message has been sent successfully.'
      );

      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });

      setAttachment(null);
      setAttachmentError(null);

    } catch (error) {
      setSubmitStatus('error');

      setSubmitMsg(
        error.response?.data?.message ||
          'Failed to send your message. Please contact us directly by phone or WhatsApp.'
      );

    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="contact-page">


      {/* =====================================================
          PAGE BANNER
          ===================================================== */}

      <section
        className="page-banner"
        style={{
          backgroundImage:
            "url('/contact-banner.png')",
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
      >
        <div className="container">

          <div className="banner-content">

            <span className="section-label">
              Annapurna Axis Trading
            </span>

            <h1>
              Contact Us
            </h1>

            <div className="breadcrumb">

              <Link to="/">
                Home
              </Link>

              <span>/</span>

              <span>
                Contact Us
              </span>

            </div>

          </div>

        </div>
      </section>



      {/* =====================================================
          CONTACT INFORMATION
          ===================================================== */}

      <section className="contact-info-section">

        <div className="container">

          <div className="contact-info-grid">


            {/* ADDRESS */}

            <div className="contact-info-card">

              <div className="contact-info-icon">
                <FaMapMarkerAlt />
              </div>

              <h3>
                Our Locations
              </h3>

              <p>
                Guangzhou, Guangdong, China
              </p>

              <p>
                Chauthe, Pokhara, Nepal
              </p>

            </div>



            {/* PHONE */}

            <div className="contact-info-card">

              <div className="contact-info-icon">
                <FaPhoneAlt />
              </div>

              <h3>
                Phone & WhatsApp
              </h3>

              <p>
                <a href="tel:+9779860105010">
                  +977 9860105010
                </a>
              </p>

              <p>
                <a href="tel:+8615030709205">
                  +86 15030709205
                </a>
              </p>

            </div>



            {/* EMAIL */}

            <div className="contact-info-card">

              <div className="contact-info-icon">
                <FaEnvelope />
              </div>

              <h3>
                Email
              </h3>

              <p>
                <a href="mailto:annapurnaaxis@gmail.com">
                  annapurnaaxis@gmail.com
                </a>
              </p>

              <p>
                <a href="mailto:info.annapurnaaxis@gmail.com">
                  info.annapurnaaxis@gmail.com
                </a>
              </p>

            </div>



            {/* WORKING HOURS */}

            <div className="contact-info-card">

              <div className="contact-info-icon">
                <FaClock />
              </div>

              <h3>
                Working Hours
              </h3>

              <p>
                Monday – Saturday
              </p>

              <p>
                9:00 AM – 6:00 PM
              </p>

              <p className="closed-note">
                Sunday: Closed
              </p>

            </div>

          </div>

        </div>

      </section>



      {/* =====================================================
          CONTACT FORM + MAP
          ===================================================== */}

      <section className="contact-main-section">

        <div className="container">

          <div className="contact-main-grid">


            {/* =================================================
                LEFT - CONTACT FORM
                ================================================= */}

            <div className="contact-form-col">

              <span className="section-label">
                Get In Touch
              </span>

              <h2 className="section-heading">
                Let's Build a Reliable Trade Connection
              </h2>

              <p className="form-intro">
                Looking for reliable product sourcing,
                quality inspection, packaging, shipping,
                customs support, or door-to-door delivery?
                Send us your requirements and our team
                will get back to you.
              </p>


              <StatusMsg
                status={submitStatus}
                message={submitMsg}
              />


              <form
                className="contact-form"
                onSubmit={handleSubmit}
              >


                {/* NAME + EMAIL */}

                <div className="form-row">

                  <div className="form-group">

                    <label htmlFor="name">
                      Your Name *
                    </label>

                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-control"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />

                  </div>


                  <div className="form-group">

                    <label htmlFor="email">
                      Email Address *
                    </label>

                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-control"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />

                  </div>

                </div>



                {/* PHONE + SERVICE */}

                <div className="form-row">

                  <div className="form-group">

                    <label htmlFor="phone">
                      Phone / WhatsApp
                    </label>

                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="form-control"
                      placeholder="+977 9860105010"
                      value={formData.phone}
                      onChange={handleChange}
                    />

                  </div>


                  <div className="form-group">

                    <label htmlFor="subject">
                      Service Required *
                    </label>

                    <select
                      id="subject"
                      name="subject"
                      className="form-control"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                    >

                      <option value="">
                        Select a service
                      </option>

                      <option value="Product Sourcing">
                        Product Sourcing
                      </option>

                      <option value="Quality Inspection">
                        Quality Inspection
                      </option>

                      <option value="Packaging">
                        Packaging
                      </option>

                      <option value="Shipping & Logistics">
                        Shipping & Logistics Management
                      </option>

                      <option value="Custom Clearance">
                        Custom Clearance Support
                      </option>

                      <option value="Door to Door Delivery">
                        Door-to-Door Delivery
                      </option>

                      <option value="Amazon FBA Delivery">
                        China to Amazon FBA Delivery
                      </option>

                      <option value="Other">
                        Other
                      </option>

                    </select>

                  </div>

                </div>



                {/* MESSAGE */}

                <div className="form-group">

                  <label htmlFor="message">
                    Message *
                  </label>

                  <textarea
                    id="message"
                    name="message"
                    className="form-control"
                    rows="6"
                    placeholder="Tell us about your product, quantity, destination, sourcing requirements, or shipping needs..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />

                </div>



                {/* ATTACHMENT */}

                <div className="form-group">

                  <label>
                    Product Details / Document
                    (Optional)
                  </label>

                  <FileDropZone
                    accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx"
                    maxSizeMB={10}
                    onFileSelect={setAttachment}
                    onFileError={setAttachmentError}
                    file={attachment}
                    label="Attach product details, quotation, specification, or reference document"
                  />

                  {attachmentError && (
                    <p className="file-error">
                      {attachmentError}
                    </p>
                  )}

                </div>



                {/* SUBMIT */}

                <button
                  type="submit"
                  className="contact-submit"
                  disabled={
                    submitting ||
                    !!attachmentError
                  }
                >

                  {submitting ? (
                    <>
                      <span className="spinner" />
                      Sending...
                    </>
                  ) : (
                    'Send Business Inquiry →'
                  )}

                </button>

              </form>

            </div>



            {/* =================================================
                RIGHT - MAPS + COMPANY INFO
                ================================================= */}

            <div className="contact-side-col">


              {/* =================================================
                  POKHARA MAP
                  ================================================= */}

              <div className="map-box">

                <h3>
                  <FaMapMarkerAlt />
                  Nepal Office
                </h3>

                <div className="map-placeholder">

                  <iframe
                    title="Annapurna Axis Trading - Pokhara Nepal"
                    src="https://www.google.com/maps?q=Chauthe,+Pokhara,+Nepal&output=embed"
                    width="100%"
                    height="280"
                    style={{
                      border: 0,
                      borderRadius: '4px',
                    }}
                    allowFullScreen
                    loading="lazy"
                  />

                </div>

                <p className="map-address">
                  📍 Chauthe, Pokhara, Nepal
                </p>

                <a
                  href="https://www.google.com/maps/search/?api=1&query=Chauthe%2C+Pokhara%2C+Nepal"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-dark-map"
                >
                  📍 View Nepal Office on Google Maps
                </a>

              </div>



              {/* =================================================
                  GUANGZHOU MAP
                  ================================================= */}

              <div className="map-box">

                <h3>
                  <FaGlobeAsia />
                  China Office
                </h3>

                <div className="map-placeholder">

                  <iframe
                    title="Annapurna Axis Trading - Guangzhou China"
                    src="https://www.google.com/maps?q=Guangzhou,+Guangdong,+China&output=embed"
                    width="100%"
                    height="280"
                    style={{
                      border: 0,
                      borderRadius: '4px',
                    }}
                    allowFullScreen
                    loading="lazy"
                  />

                </div>

                <p className="map-address">
                  📍 Guangzhou, Guangdong, China
                </p>

                <a
                  href="https://www.google.com/maps/search/?api=1&query=Guangzhou%2C+Guangdong%2C+China"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-dark-map"
                >
                  📍 View China Office on Google Maps
                </a>

              </div>



              {/* =================================================
                  WHY US
                  ================================================= */}

              <div className="contact-side-card">

                <h3>
                  Why Choose Annapurna Axis?
                </h3>

                <ul className="contact-why-list">

                  <li>
                    Trusted supplier sourcing
                  </li>

                  <li>
                    Quality inspection before shipping
                  </li>

                  <li>
                    Secure and suitable packaging
                  </li>

                  <li>
                    International shipping coordination
                  </li>

                  <li>
                    Customs clearance support
                  </li>

                  <li>
                    Door-to-door delivery
                  </li>

                  <li>
                    China-to-Amazon FBA delivery
                  </li>

                </ul>

              </div>

            </div>

          </div>

        </div>

      </section>



      {/* =====================================================
          TRUST / SERVICES BAR
          ===================================================== */}

      <section className="trust-bar">

        <div className="trust-item">

          <FaSearch className="trust-icon" />

          <span>
            Product Sourcing
          </span>

        </div>


        <div className="trust-item">

          <FaShieldAlt className="trust-icon" />

          <span>
            Quality Inspection
          </span>

        </div>


        <div className="trust-item">

          <FaBoxOpen className="trust-icon" />

          <span>
            Packaging
          </span>

        </div>


        <div className="trust-item">

          <FaTruck className="trust-icon" />

          <span>
            Shipping & Logistics
          </span>

        </div>


        <div className="trust-item">

          <FaFileAlt className="trust-icon" />

          <span>
            Customs Support
          </span>

        </div>


        <div className="trust-item">

          <FaHome className="trust-icon" />

          <span>
            Door-to-Door Delivery
          </span>

        </div>


        <div className="trust-item">

          <FaGlobeAsia className="trust-icon" />

          <span>
            Amazon FBA Delivery
          </span>

        </div>

      </section>



      {/* =====================================================
          FINAL CTA
          ===================================================== */}

      <section className="about-cta">

        <div className="about-cta-content">

          <span className="section-label">
            Start Your Global Trade Journey
          </span>

          <h2>
            Source. Inspect. Ship. Deliver.
          </h2>

          <p>
            Let Annapurna Axis Trading handle
            the complexities of international sourcing
            and logistics while you focus on growing
            your business.
          </p>

          <a
            href="https://wa.me/9779860105010"
            target="_blank"
            rel="noreferrer"
            className="btn-dark-map"
          >
            Contact Us on WhatsApp
          </a>

        </div>

      </section>

    </div>
  );
};

export default Contact;